/**
 * Professional SVG icons for admin sidebar – each path maps to a matching icon.
 * Outline style, 24x24, consistent stroke.
 */

import React from 'react';

const iconProps = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

export function getSidebarIcon(path: string): React.ReactNode {
  const Icons: Record<string, React.ReactNode> = {
    'role-access': <LogIn {...iconProps} />,
    'system-dashboard': <LayoutDashboard {...iconProps} />,
    'ai-advisor': <Sparkles {...iconProps} />,
    'global-settings': <Settings {...iconProps} />,
    'tariff-plans': <Tag {...iconProps} />,
    'landing-team': <Users {...iconProps} />,
    'tariff-plan': <Tag {...iconProps} />,
    'integrations': <Plug {...iconProps} />,
    'tenant-management': <Building {...iconProps} />,
    'tenant-messages': <Inbox {...iconProps} />,
    'staff-chat': <Send {...iconProps} />,
    'ombor': <Package {...iconProps} />,
    'feature-flags': <Flag {...iconProps} />,
    'audit-logs': <ClipboardList {...iconProps} />,
    'system-health': <Activity {...iconProps} />,
    'usage-metrics': <BarChart2 {...iconProps} />,
    'error-monitoring': <AlertCircle {...iconProps} />,
    'ai-chat': <Sparkles {...iconProps} />,
    'cashier-chat': <MessageCircle {...iconProps} />,
    'marketing': <Megaphone {...iconProps} />,
    'marketing-orders': <ShoppingBag {...iconProps} />,
    'desktop-apps': <Monitor {...iconProps} />,
    'managers': <Users {...iconProps} />,
    'rating': <TrendingUp {...iconProps} />,
    'rating-dispatchers': <TrendingUp {...iconProps} />,
    'dispatchers': <Users {...iconProps} />,
    'statistics': <BarChart2 {...iconProps} />,
    'leads': <ClipboardList {...iconProps} />,
    'to-managers': <Send {...iconProps} />,
    'cashiers': <Users {...iconProps} />,
    'staff-workers': <Briefcase {...iconProps} />,
    'company-dashboard': <LayoutDashboard {...iconProps} />,
    'branches': <GitBranch {...iconProps} />,
    'warehouses': <Package {...iconProps} />,
    'employees-roles': <Users {...iconProps} />,
    'products': <Package {...iconProps} />,
    'categories': <Folder {...iconProps} />,
    'price-management': <DollarSign {...iconProps} />,
    'customers': <Users {...iconProps} />,
    'orders': <ShoppingCart {...iconProps} />,
    'deliveries': <Truck {...iconProps} />,
    'reports': <FileText {...iconProps} />,
    'notifications': <Bell {...iconProps} />,
    'company-settings': <Settings {...iconProps} />,
    'sessions': <Key {...iconProps} />,
    'settings': <Settings {...iconProps} />,
    'help': <HelpCircle {...iconProps} />,
    'kassa': <Monitor {...iconProps} />,
    'retail-clients': <Users {...iconProps} />,
    'pos-orders': <Monitor {...iconProps} />,
    'manager-customers': <Users {...iconProps} />,
    'manager-payments': <CreditCard {...iconProps} />,
    'cashier-z-report': <FileText {...iconProps} />,
    'owner-z-reports': <FileText {...iconProps} />,
    'product-audit': <ClipboardList {...iconProps} />,
    'expenses': <CreditCard {...iconProps} />,
    'order-history': <History {...iconProps} />,
    'delivery-status': <MapPin {...iconProps} />,
    'product-catalog': <List {...iconProps} />,
    'ombor-products': <Package {...iconProps} />,
    'inventory-dashboard': <LayoutDashboard {...iconProps} />,
    'stock-in': <ArrowDownCircle {...iconProps} />,
    'stock-out': <ArrowUpCircle {...iconProps} />,
    'transfers': <ArrowLeftRight {...iconProps} />,
    'barcode-scanner': <ScanBarcode {...iconProps} />,
    'inventory-audit': <ClipboardCheck {...iconProps} />,
    'expiry-tracking': <Calendar {...iconProps} />,
    'finance-dashboard': <LayoutDashboard {...iconProps} />,
    'transactions': <Repeat {...iconProps} />,
    'invoices': <FileText {...iconProps} />,
    'taxes': <Percent {...iconProps} />,
    'profit-loss': <TrendingUp {...iconProps} />,
    'balance-sheet': <Layers {...iconProps} />,
    'export-reports': <Download {...iconProps} />,
    'analytics-dashboard': <LayoutDashboard {...iconProps} />,
    'sales-funnel': <Filter {...iconProps} />,
    'customer-segments': <PieChart {...iconProps} />,
    'campaigns': <Megaphone {...iconProps} />,
    'ai-recommendations': <Sparkles {...iconProps} />,
    'retention': <RefreshCw {...iconProps} />,
    'cohort-analysis': <Layers {...iconProps} />,
    'my-deliveries': <Truck {...iconProps} />,
    'route-map': <Map {...iconProps} />,
    'courier-order-status': <ClipboardList {...iconProps} />,
    'offline-sync': <WifiOff {...iconProps} />,
    'earnings': <DollarSign {...iconProps} />,
    'my-orders': <ShoppingBag {...iconProps} />,
    'customer-payments': <CreditCard {...iconProps} />,
    'profile': <User {...iconProps} />,
    'locations': <MapPin {...iconProps} />,
  };
  return Icons[path] ?? <LayoutDashboard {...iconProps} />;
}

function LogIn(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </svg>
  );
}

function LayoutDashboard(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  );
}
function Settings(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function Tag(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}
function Plug(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M12 22v-5" />
      <path d="M9 8V2" />
      <path d="M15 8V2" />
      <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
    </svg>
  );
}
function Building(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M8 10h.01" />
      <path d="M16 10h.01" />
      <path d="M8 14h.01" />
      <path d="M16 14h.01" />
    </svg>
  );
}
function Flag(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  );
}
function ClipboardList(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </svg>
  );
}
function Activity(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}
function BarChart2(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}
function AlertCircle(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
function Key(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 2 2L22 7l-2-2m-4.5 4.5L19 4" />
    </svg>
  );
}
function GitBranch(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </svg>
  );
}
function Package(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="m7.5 4.27 9 5.15" />
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  );
}
function Briefcase(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}
function Users(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function Folder(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}
function DollarSign(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}
function ShoppingCart(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
    </svg>
  );
}
function Truck(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M5 18H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v11" />
      <path d="M14 18h2c.6 0 1-.4 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 0-1.21.502l-1.182 2.366" />
      <path d="M19 18h2c.6 0 1-.4 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 0-1.21.502l-1.182 2.366" />
    </svg>
  );
}
function FileText(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
}
function Bell(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
function Monitor(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}
function CreditCard(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
}
function History(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}
function MapPin(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function List(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}
function ArrowDownCircle(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v8" />
      <path d="m8 12 4 4 4-4" />
    </svg>
  );
}
function ArrowUpCircle(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16V8" />
      <path d="m8 12 4-4 4 4" />
    </svg>
  );
}
function ArrowLeftRight(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="m16 3 4 4-4 4" />
      <path d="M20 7H4" />
      <path d="m8 21-4-4 4-4" />
      <path d="M4 17h16" />
    </svg>
  );
}
function ScanBarcode(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 12h10" />
      <path d="M7 8h1" />
      <path d="M7 16h1" />
      <path d="M10 8h1" />
      <path d="M10 16h1" />
      <path d="M13 8h1" />
      <path d="M13 16h1" />
      <path d="M16 8h1" />
      <path d="M16 16h1" />
    </svg>
  );
}
function ClipboardCheck(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}
function Calendar(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function Repeat(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="m17 1 4 4-4 4" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <path d="m7 23-4-4 4-4" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}
function Percent(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
function Database(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    </svg>
  );
}
function TrendingUp(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}
function Layers(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
    </svg>
  );
}
function Download(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
function Filter(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
function PieChart(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  );
}
function Megaphone(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}
function Sparkles(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    </svg>
  );
}
function RefreshCw(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1 6.74-2.74L21 16" />
      <path d="M21 21v-5h-5" />
    </svg>
  );
}
function Map(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
      <line x1="9" y1="3" x2="9" y2="18" />
      <line x1="15" y1="6" x2="15" y2="21" />
    </svg>
  );
}
function WifiOff(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <line x1="2" y1="2" x2="22" y2="22" />
      <path d="M8.5 16.429a5 5 0 0 1 7 0" />
      <path d="M5 12.859a10 10 0 0 1 5.17-2.69" />
      <path d="M19 12.859a10 10 0 0 0-2.007-1.923" />
      <path d="M12 20h.01" />
    </svg>
  );
}
function ShoppingBag(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}
function User(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function MessageCircle(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}
function Inbox(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}
function Send(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <path d="M22 2L11 13" />
      <path d="M22 2L15 22L11 13L2 9L22 2Z" />
    </svg>
  );
}
function HelpCircle(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-2 3-2 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}
